import { EN_RECIPE_OVERRIDES, TR_RECIPE_OVERRIDES } from "./recipeOverrides.js";

const PLACEHOLDER_PATTERN = /(^|[\s"'])ana malzeme|tarife uygun|dolmalık sebze veya yaprak|main protein|main vegetables|main ingredients|main ingredient|chosen side ingredient|main vegetables or protein|main protein or vegetables/i;
const GENERIC_RECIPE_PATTERN = /bread, buns, wraps or rolls|cooked meat, fish, eggs, beans or cheese|vegetables or sauce ingredients|eggs or pastry base|vegetables, cheese or cooked meat|fresh salad greens or sliced vegetables|600 g beef, chicken, pork or vegetables|500 g chicken, beef, shrimp, tofu or eggs|500 g chicken, beef, beans or cheese|tarife uygun hamur|iç harç için kıyma, peynir, patates ya da sebze|600 g kuşbaşı et veya kıyma|2 su bardağı haşlanmış bakliyat|i̇steğe göre et veya kıyma|yanına patates veya sebze|600 g tavuk/i;

const normalize = (value, locale) =>
  String(value || "")
    .trim()
    .toLocaleLowerCase(locale === "tr" ? "tr-TR" : "en-US");

const includesAny = (value, terms) => terms.some((term) => value.includes(term));

const unique = (values = []) => {
  const seen = new Set();
  return values.filter((value) => {
    const key = String(value || "").trim().toLocaleLowerCase("tr-TR");
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const isGenericRecipe = (recipe) => {
  const serialized = JSON.stringify(recipe || {});
  return PLACEHOLDER_PATTERN.test(serialized) || GENERIC_RECIPE_PATTERN.test(serialized);
};

const getCategory = (dishName, recipe, locale) => {
  const name = normalize(dishName, locale);
  const category = normalize(recipe?.kategori, locale);

  if (locale === "en" && includesAny(name, ["dublin coddle"])) return "meat";
  if (locale === "en" && includesAny(name, ["pancake", "french toast", "omelette", "frittata", "quiche", "shakshuka", "breakfast-for-dinner"])) return "egg";
  if (locale === "en" && includesAny(name, ["shepherd's pie", "cottage pie"])) return "groundMeat";
  if (locale === "en" && name.includes("moussaka")) return "groundMeat";
  if (locale === "en" && includesAny(name, ["pasta", "spaghetti", "macaroni", "lasagna", "ziti", "gnocchi", "pizza", "calzone", "noodle", "orzo", " rice", "risotto", "paella", "jambalaya"])) return "starch";
  if (locale === "tr" && includesAny(name, ["karnıyarık", "patlıcan musakka", "patates oturtma"])) return "groundMeat";
  if (locale === "tr" && includesAny(name, ["makarna", "mantı", "erişte", "börek", "gözleme", "pide", "lahmacun", "etli ekmek", "pilav"])) return "starch";
  if (locale === "tr" && includesAny(name, ["fasulye", "nohut", "mercimek", "barbunya", "börülce"])) return "legume";

  if (includesAny(name, locale === "tr"
    ? ["çorbası", "çorba"]
    : ["soup", "chowder", "pasta e fagioli"])) return "soup";
  if (includesAny(name, locale === "tr"
    ? ["salatası", "salata", "cacık", "haydari", "tarator", "turşu", "söğüş", "piyaz"]
    : ["salad", "slaw", "greens", "vegetable sticks", "fruit plate", "dinner rolls", "garlic bread", "corn on the cob", "green beans", "roasted vegetables", "breakfast tomatoes"])) return "side";
  if (includesAny(name, locale === "tr"
    ? ["balık", "hamsi", "palamut", "levrek", "çipura", "alabalık", "somon", "uskumru"]
    : ["fish", "salmon", "baked cod", "cod fishcake", "tilapia", "shrimp", "tuna", "seafood", "clam"])) return "fish";
  if (includesAny(name, locale === "tr"
    ? ["tavuk"]
    : ["chicken", "turkey", "duck"])) return "poultry";
  if (includesAny(name, locale === "tr"
    ? ["köfte", "kıyma", "kıymalı"]
    : ["meatball", "meatloaf", "minced", "burger", "sloppy joe"])) return "groundMeat";
  if (includesAny(name, locale === "tr"
    ? ["kebap", "kavurma", "tandır", "etli", "et sote", "kuzu", "dana", "haşlama", "beğendi", "ali nazik", "çökertme"]
    : ["beef", "steak", "pork", "sausage", "bratwurst", "kielbasa", "goulash", "bourguignon", "roast", "coddle", "bangers", "toad in the hole", "bacon", "blt", "reuben", "croque monsieur"])) return "meat";
  if (includesAny(name, locale === "tr"
    ? ["makarna", "mantı", "erişte", "börek", "gözleme", "pide", "lahmacun", "etli ekmek"]
    : ["pasta", "spaghetti", "macaroni", "lasagna", "ziti", "gnocchi", "pizza", "calzone", "noodle", "pierogi", "orzo"])) return "starch";
  if (includesAny(name, locale === "tr"
    ? ["fasulye", "nohut", "mercimek", "barbunya", "börülce"]
    : ["bean", "lentil", "chickpea", "dahl", "falafel"])) return "legume";
  if (includesAny(name, locale === "tr"
    ? ["yumurta", "menemen", "çılbır"]
    : ["egg", "omelette", "frittata", "quiche", "shakshuka", "pancake", "french toast"])) return "egg";
  if (category.includes(locale === "tr" ? "çorba" : "soup")) return "soup";
  if (category.includes(locale === "tr" ? "salata" : "side") || category.includes("starter")) return "side";
  return "vegetable";
};

const trPrimary = (dishName, category) => {
  const name = normalize(dishName, "tr");
  if (category === "groundMeat") return { ingredient: "500 g orta yağlı kıyma", label: "kıyma" };
  if (category === "poultry") return { ingredient: "600 g kemiksiz tavuk eti", label: "tavuk" };
  if (category === "meat") return { ingredient: "600 g kuşbaşı dana veya kuzu eti", label: "et" };
  if (category === "starch") {
    if (name.includes("mantı")) return { ingredient: "3 su bardağı un", label: "mantı hamuru" };
    if (includesAny(name, ["börek", "gözleme"])) return { ingredient: "4 adet yufka", label: "yufka" };
    if (includesAny(name, ["pide", "lahmacun", "etli ekmek"])) return { ingredient: "4 su bardağı un", label: "hamur" };
    if (name.includes("pilav")) return { ingredient: "2 su bardağı pirinç", label: "pirinç" };
    return { ingredient: "400 g makarna veya erişte", label: "makarna" };
  }
  const mappings = [
    [["patlıcan"], ["4 orta boy patlıcan", "patlıcan"]],
    [["kabak"], ["4 orta boy kabak", "kabak"]],
    [["patates"], ["5 orta boy patates", "patates"]],
    [["bezelye"], ["500 g ayıklanmış bezelye", "bezelye"]],
    [["ıspanak"], ["750 g ayıklanmış ıspanak", "ıspanak"]],
    [["kapuska", "lahana"], ["1 orta boy beyaz lahana", "lahana"]],
    [["pırasa"], ["1 kg temizlenmiş pırasa", "pırasa"]],
    [["kereviz"], ["4 orta boy kereviz", "kereviz"]],
    [["bamya"], ["750 g ayıklanmış bamya", "bamya"]],
    [["karnabahar"], ["1 orta boy karnabahar", "karnabahar"]],
    [["kuru fasulye"], ["400 g kuru fasulye", "kuru fasulye"]],
    [["taze fasulye"], ["750 g ayıklanmış taze fasulye", "taze fasulye"]],
    [["fasulye"], ["500 g fasulye", "fasulye"]],
    [["nohut"], ["2 su bardağı haşlanmış nohut", "nohut"]],
    [["mercimek"], ["2 su bardağı haşlanmış mercimek", "mercimek"]],
    [["barbunya"], ["2 su bardağı haşlanmış barbunya", "barbunya"]],
    [["börülce"], ["2 su bardağı haşlanmış börülce", "börülce"]],
    [["tavuk"], ["600 g kemiksiz tavuk eti", "tavuk"]],
    [["köfte", "kıyma", "kıymalı"], ["500 g orta yağlı kıyma", "kıyma"]],
    [["hamsi"], ["1 kg ayıklanmış hamsi", "hamsi"]],
    [["palamut"], ["2 adet dilimlenmiş palamut", "palamut"]],
    [["somon"], ["4 adet 150 g somon fileto", "somon"]],
    [["balık", "levrek", "çipura", "alabalık", "uskumru"], ["4 porsiyon temizlenmiş balık", "balık"]],
    [["makarna", "erişte"], ["400 g makarna veya erişte", "makarna"]],
    [["yumurta", "menemen", "çılbır"], ["6 adet yumurta", "yumurta"]],
    [["zeytinyağlı", "sebze", "türlü", "güveç"], ["1 kg mevsim sebzesi", "sebze"]],
  ];
  const found = mappings.find(([terms]) => includesAny(name, terms));
  if (found) return { ingredient: found[1][0], label: found[1][1] };
  return { ingredient: `800 g ${dishName} için temel sebze`, label: dishName };
};

const enPrimary = (dishName, category) => {
  const name = normalize(dishName, "en");
  const mappings = [
    [["tomato"], ["800 g ripe tomatoes or 2 x 400 g cans", "tomatoes"]],
    [["carrot"], ["600 g carrots", "carrots"]],
    [["red pepper soup"], ["5 large red bell peppers", "red bell peppers"]],
    [["butternut"], ["1 kg peeled butternut squash", "butternut squash"]],
    [["broccoli"], ["500 g broccoli florets", "broccoli"]],
    [["cauliflower"], ["1 medium cauliflower", "cauliflower"]],
    [["potato"], ["800 g potatoes", "potatoes"]],
    [["green beans"], ["500 g trimmed green beans", "green beans"]],
    [["corn"], ["4 corn cobs or 500 g corn kernels", "corn"]],
    [["salmon"], ["4 x 150 g salmon fillets", "salmon"]],
    [["shrimp"], ["500 g peeled raw shrimp", "shrimp"]],
    [["tuna"], ["2 x 145 g cans tuna, drained", "tuna"]],
    [["fish", "cod", "tilapia", "seafood", "clam"], ["4 x 150 g fish fillets or 500 g seafood", "fish or seafood"]],
    [["chicken"], ["600 g boneless chicken", "chicken"]],
    [["turkey"], ["600 g turkey", "turkey"]],
    [["beef", "steak"], ["600 g beef", "beef"]],
    [["pork", "ham", "sausage", "bratwurst", "kielbasa"], ["600 g pork or sausage specified by the dish", "pork or sausage"]],
    [["bangers", "toad in the hole"], ["8 good-quality pork sausages", "sausages"]],
    [["blt"], ["12 rashers bacon", "bacon"]],
    [["club sandwich"], ["400 g cooked chicken and 8 rashers bacon", "chicken and bacon"]],
    [["croque monsieur"], ["8 slices ham and 150 g Gruyère or cheddar", "ham and cheese"]],
    [["reuben"], ["400 g sliced corned beef", "corned beef"]],
    [["cottage pie"], ["600 g lean minced beef", "minced beef"]],
    [["moussaka"], ["3 medium aubergines and 500 g minced lamb or beef", "aubergines and mince"]],
    [["stuffed bell pepper"], ["4 large bell peppers", "bell peppers"]],
    [["stuffed zucchini"], ["4 medium zucchini", "zucchini"]],
    [["pizza", "flatbread"], ["4 individual pizza or flatbread bases", "pizza bases"]],
    [["pierogi"], ["500 g fresh or frozen pierogi", "pierogi"]],
    [["falafel"], ["2 x 400 g cans chickpeas, drained", "chickpeas"]],
    [["pasta", "spaghetti", "macaroni", "ziti", "gnocchi", "noodle", "orzo"], ["400 g pasta, gnocchi, noodles or orzo specified by the dish", "pasta or noodles"]],
    [["rice", "risotto", "paella", "jambalaya"], ["300 g long-grain or risotto rice", "rice"]],
    [["lentil", "dahl"], ["250 g dried lentils", "lentils"]],
    [["bean", "chickpea"], ["2 x 400 g cans beans or chickpeas, drained", "beans or chickpeas"]],
    [["egg", "omelette", "frittata", "quiche", "shakshuka"], ["8 large eggs", "eggs"]],
    [["salad", "greens", "slaw"], ["600 g fresh salad vegetables", "salad vegetables"]],
  ];
  const found = mappings.find(([terms]) => includesAny(name, terms));
  if (found) return { ingredient: found[1][0], label: found[1][1] };
  if (category === "poultry") return { ingredient: "600 g chicken or turkey specified by the dish", label: "poultry" };
  if (category === "meat" || category === "groundMeat") return { ingredient: "600 g meat specified by the dish", label: "meat" };
  if (category === "egg") return { ingredient: "8 large eggs", label: "eggs" };
  if (category === "legume") return { ingredient: "2 x 400 g cans beans, lentils or chickpeas specified by the dish", label: "legumes" };
  if (category === "starch") return { ingredient: `500 g base ingredient specified for ${dishName}`, label: dishName };
  if (category === "side") return { ingredient: `600 g fresh ingredients specified for ${dishName}`, label: dishName };
  if (category === "soup") return { ingredient: `800 g mixed vegetables for ${dishName}`, label: "vegetables" };
  return { ingredient: `800 g vegetables specified for ${dishName}`, label: dishName };
};

const dishSpecificExtras = (dishName, locale) => {
  const name = normalize(dishName, locale);
  const extras = [];
  const add = (condition, values) => { if (condition) extras.push(...values); };

  if (locale === "tr") {
    add(name.includes("pilav"), ["2 su bardağı pirinç", "3 su bardağı sıcak su veya tavuk suyu"]);
    add(includesAny(name, ["dolma", "sarma"]), ["1½ su bardağı pirinç", "1 yemek kaşığı domates salçası", "1 tatlı kaşığı kuru nane"]);
    add(includesAny(name, ["börek", "gözleme"]), ["4 adet yufka", "300 g iç harç için peynir, patates veya ıspanak"]);
    add(name.includes("mantı"), ["3 su bardağı un", "1 adet yumurta", "250 g kıyma", "Servis için 500 g yoğurt ve 2 diş sarımsak"]);
    add(includesAny(name, ["pide", "lahmacun", "etli ekmek"]), ["4 su bardağı un", "1 paket instant maya", "350 g kıyma", "2 adet domates ve 2 adet biber"]);
    add(includesAny(name, ["makarna", "erişte"]), ["400 g makarna veya erişte", name.includes("yoğurtlu") ? "500 g yoğurt ve 2 diş sarımsak" : "2 su bardağı yemek adına uygun sos"]);
    add(includesAny(name, ["karnıyarık", "musakka", "imam bayıldı"]), ["4 orta boy patlıcan", "2 adet domates", "2 adet sivri biber"]);
    add(includesAny(name, ["hünkâr beğendi", "ali nazik"]), ["4 orta boy közlenmiş patlıcan", "2 yemek kaşığı un", "2 su bardağı süt", "100 g rendelenmiş kaşar peyniri"]);
    return extras;
  }

  add(includesAny(name, ["rice", "risotto", "paella", "jambalaya"]), ["300 g rice", "750 ml / 3 cups low-salt stock"]);
  add(includesAny(name, ["pasta", "spaghetti", "macaroni", "ziti", "gnocchi", "noodle", "orzo", "lasagna"]), ["400 g pasta, noodles, gnocchi or orzo specified by the dish"]);
  add(includesAny(name, ["taco", "fajita", "burrito", "quesadilla", "enchilada"]), ["8 medium tortillas", "1 bell pepper", "200 g tomato salsa"]);
  add(includesAny(name, ["pizza", "flatbread", "calzone"]), ["4 individual pizza bases or 500 g pizza dough", "300 ml tomato sauce", "200 g mozzarella"]);
  add(includesAny(name, ["sandwich", "burger", "wrap", "toasties", "panini", "sub", "cheesesteak", "french dip", "reuben", "croque"]), ["8 slices bread, 4 buns or 4 wraps specified by the dish", "4 lettuce leaves or 2 sliced tomatoes"]);
  add(includesAny(name, ["pie", "shepherd"]), ["800 g mashed potatoes or 500 g pastry specified by the dish", "500 ml / 2 cups thick gravy or sauce"]);
  add(includesAny(name, ["curry", "tikka", "madras", "dahl"]), ["2 tbsp curry paste or ground spice blend", "400 ml coconut milk or 500 ml stock"]);
  add(includesAny(name, ["stir-fry", "teriyaki", "soy", "fried rice", "mongolian", "korean", "bibimbap"]), ["3 tbsp reduced-salt soy sauce", "1 tbsp grated ginger", "2 garlic cloves"]);
  add(includesAny(name, ["alfredo", "cheddar", "cheese", "creamy"]), ["500 ml milk or cream", "150 g grated cheese"]);
  add(name.includes("pesto"), ["120 g basil pesto"]);
  add(includesAny(name, ["bolognese", "ragu"]), ["500 g lean minced beef or turkey", "2 x 400 g cans chopped tomatoes"]);
  add(name.includes("bangers and mash"), ["1 kg potatoes", "60 g butter", "150 ml milk", "500 ml onion gravy"]);
  add(name.includes("toad in the hole"), ["140 g plain flour", "4 large eggs", "200 ml milk", "1 tbsp cooking oil"]);
  add(name.includes("croque monsieur"), ["8 slices sourdough or sandwich bread", "30 g butter", "250 ml béchamel sauce"]);
  add(name.includes("moussaka"), ["3 medium aubergines", "500 g minced lamb or beef", "2 x 400 g cans chopped tomatoes", "500 ml béchamel sauce"]);
  add(name.includes("cobb salad"), ["300 g cooked chicken", "6 rashers cooked bacon", "4 hard-boiled eggs", "1 avocado"]);
  add(name.includes("chef salad"), ["250 g cooked chicken or turkey", "150 g ham", "4 hard-boiled eggs", "120 g cheddar"]);
  add(name.includes("grilled chicken salad"), ["500 g chicken breast", "600 g mixed salad vegetables"]);
  add(name.includes("tuna pasta salad"), ["300 g pasta", "2 x 145 g cans tuna, drained", "300 g salad vegetables"]);
  add(name.includes("chicken pasta salad"), ["300 g pasta", "400 g cooked chicken", "300 g salad vegetables"]);
  return extras;
};

const trIngredientBundle = (dishName, category, primary) => {
  const common = [primary.ingredient, "1 orta boy kuru soğan", "2 yemek kaşığı zeytinyağı veya sıvı yağ", "1 çay kaşığı tuz", "½ çay kaşığı karabiber"];
  const bundles = {
    soup: [primary.ingredient, "1 orta boy kuru soğan", "1 yemek kaşığı tereyağı veya zeytinyağı", "1 litre sıcak su veya tuzsuz et/sebze suyu", "1 çay kaşığı tuz", "½ çay kaşığı karabiber"],
    side: [primary.ingredient, "2 yemek kaşığı zeytinyağı", "1 yemek kaşığı limon suyu veya sirke", "½ çay kaşığı tuz", "2 yemek kaşığı kıyılmış maydanoz", "İsteğe göre 3 yemek kaşığı yoğurt"],
    fish: [primary.ingredient, "2 yemek kaşığı zeytinyağı", "1 limon", "1 çay kaşığı tuz", "½ çay kaşığı karabiber", "½ demet maydanoz veya roka"],
    poultry: [primary.ingredient, "1 orta boy kuru soğan", "2 adet sivri biber", "2 yemek kaşığı sıvı yağ", "1 yemek kaşığı domates salçası", "1 çay kaşığı tuz", "½ çay kaşığı karabiber ve kekik"],
    groundMeat: [primary.ingredient, "1 küçük kuru soğan", "1 adet yumurta", "4 yemek kaşığı galeta unu", "1 çay kaşığı tuz", "½ çay kaşığı karabiber", "½ çay kaşığı kimyon"],
    meat: [primary.ingredient, "1 orta boy kuru soğan", "2 adet sivri biber", "2 yemek kaşığı sıvı yağ", "1 yemek kaşığı domates salçası", "2 su bardağı sıcak su", "1 çay kaşığı tuz ve ½ çay kaşığı karabiber"],
    starch: [primary.ingredient, "1 orta boy kuru soğan", "2 yemek kaşığı zeytinyağı veya tereyağı", "300 g iç harç için kıyma, peynir veya sebze", "1 çay kaşığı tuz", "½ çay kaşığı karabiber", "Servis için 1 su bardağı yoğurt veya salata"],
    legume: [primary.ingredient, "1 orta boy kuru soğan", "1 yemek kaşığı domates salçası", "2 yemek kaşığı sıvı yağ", "3 su bardağı sıcak su", "1 çay kaşığı tuz", "½ çay kaşığı karabiber"],
    egg: [primary.ingredient, "2 orta boy domates veya 1 su bardağı yoğurt", "2 adet sivri biber", "1 yemek kaşığı tereyağı", "½ çay kaşığı tuz", "¼ çay kaşığı karabiber ve pul biber"],
    vegetable: [...common, "1 yemek kaşığı domates salçası", "1½ su bardağı sıcak su"],
  };
  return bundles[category] || bundles.vegetable;
};

const enIngredientBundle = (dishName, category, primary) => {
  const name = normalize(dishName, "en");
  if (name.includes("pancakes")) {
    return ["250 g / 2 cups plain flour", "2 large eggs", "500 ml / 2 cups milk", "1 tbsp baking powder", "1 tbsp melted butter", "8 rashers bacon", "Fresh fruit for serving"];
  }
  if (name.includes("french toast")) {
    return ["8 thick slices bread", "4 large eggs", "250 ml / 1 cup milk", "1 tsp vanilla", "1 tsp cinnamon", "1 tbsp butter", "400 g fresh fruit"];
  }
  if (includesAny(name, ["shepherd's pie", "cottage pie"])) {
    return [primary.ingredient, "1 medium onion", "2 carrots", "150 g peas", "1 tbsp tomato paste", "500 ml / 2 cups low-salt stock", "800 g mashed potatoes"];
  }
  if (name.includes("moussaka")) {
    return [primary.ingredient, "1 medium onion", "2 x 400 g cans chopped tomatoes", "500 ml milk", "40 g flour", "40 g butter", "100 g grated cheese"];
  }
  const bundles = {
    soup: [primary.ingredient, "1 medium onion, diced", "1 tbsp olive oil or butter", "1 litre / 4 cups low-salt stock", "1 tsp salt", "½ tsp black pepper"],
    side: [primary.ingredient, "2 tbsp olive oil", "1 tbsp lemon juice or vinegar", "½ tsp salt", "¼ tsp black pepper", "2 tbsp chopped fresh herbs"],
    fish: [primary.ingredient, "2 tbsp olive oil or butter", "1 lemon", "1 tsp salt", "½ tsp black pepper", "2 tbsp chopped parsley or dill"],
    poultry: [primary.ingredient, "1 medium onion", "2 garlic cloves", "2 tbsp olive oil", "500 g mixed vegetables", "1 tsp salt", "½ tsp black pepper and dried herbs"],
    groundMeat: [primary.ingredient, "1 small onion, finely diced", "1 large egg", "60 g breadcrumbs", "1 tsp salt", "½ tsp black pepper", "1 tsp dried herbs or mild spices"],
    meat: [primary.ingredient, "1 medium onion", "2 carrots", "2 tbsp cooking oil", "500 ml / 2 cups low-salt stock", "1 tsp salt", "½ tsp black pepper and dried herbs"],
    starch: [primary.ingredient, "1 medium onion or 2 garlic cloves", "2 tbsp olive oil or butter", "500 ml / 2 cups tomato, cheese or stock-based sauce", "1 tsp salt", "½ tsp black pepper", "60 g grated cheese or fresh herbs to finish"],
    legume: [primary.ingredient, "1 medium onion", "2 garlic cloves", "1 tbsp olive oil", "1 x 400 g can chopped tomatoes", "500 ml / 2 cups low-salt stock", "1 tsp mild spices and salt to taste"],
    egg: [primary.ingredient, "250 g vegetables specified by the dish", "1 tbsp olive oil or butter", "60 ml / ¼ cup milk", "½ tsp salt", "¼ tsp black pepper", "60 g grated cheese if appropriate"],
    vegetable: [primary.ingredient, "1 medium onion", "2 garlic cloves", "2 tbsp olive oil", "1 x 400 g can chopped tomatoes or 500 ml stock", "1 tsp salt", "½ tsp black pepper and dried herbs"],
  };
  return bundles[category] || bundles.vegetable;
};

const cleanIngredient = (ingredient, primary, locale) => {
  const value = String(ingredient || "").trim();
  if (!PLACEHOLDER_PATTERN.test(value)) return value;
  if (locale === "tr") return primary.ingredient;
  return primary.ingredient;
};

const getIngredients = (dishName, recipe, category, locale, primary, genericRecipe) => {
  const existing = (recipe?.malzemeler || []).map((item) => cleanIngredient(item, primary, locale));
  const bundle = locale === "tr"
    ? trIngredientBundle(dishName, category, primary)
    : enIngredientBundle(dishName, category, primary);
  const extras = dishSpecificExtras(dishName, locale);
  if (!genericRecipe && existing.length >= 6) return unique(existing);
  const ingredients = genericRecipe
    ? unique([primary.ingredient, ...extras, ...bundle])
    : unique([...existing, ...extras, ...bundle]);
  return ingredients.slice(0, Math.max(8, existing.length));
};

const cleanStep = (step, primary, locale) => {
  let value = String(step || "").trim();
  if (locale === "tr") {
    value = value
      .replace(/Ana malzemeyi/gi, primary.label)
      .replace(/ana malzemeyi/gi, primary.label)
      .replace(/ana malzeme/gi, primary.label)
      .replace(/tarife göre/gi, "yemeğin yöntemine uygun olarak");
  } else {
    value = value
      .replace(/the chosen side ingredient/gi, primary.label)
      .replace(/chosen side ingredient/gi, primary.label)
      .replace(/the main ingredients?/gi, primary.label)
      .replace(/main ingredients?/gi, primary.label)
      .replace(/main protein or vegetables/gi, primary.label)
      .replace(/main vegetables or protein/gi, primary.label);
  }
  return value;
};

const safetyFor = (category, dishName, locale) => {
  const name = normalize(dishName, locale);
  if (locale === "tr") {
    if (category === "poultry" || name.includes("tavuk")) return "Tavuğun en kalın bölümünün iç sıcaklığı en az 74°C olmalı; çiğ tavukla temas eden ekipmanı ayrıca yıkayın.";
    if (category === "groundMeat" || includesAny(name, ["köfte", "kıyma", "kıymalı", "mantı", "lahmacun"])) return "Kıyma ve köftelerin merkez sıcaklığı en az 71°C olmalı; iç kısmı çiğ kalmamalı.";
    if (category === "fish" || includesAny(name, ["balık", "hamsi", "palamut", "levrek", "çipura", "alabalık", "somon", "uskumru"])) return "Balığın merkez sıcaklığı 63°C'ye ulaşmalı veya eti tamamen matlaşarak kolayca ayrılmalı.";
    if (category === "meat") return "Bütün parça dana/kuzu eti en az 63°C'ye ulaştıktan sonra 3 dakika dinlendirilmeli; kıymalı ürünlerde 71°C hedeflenmeli.";
    if (category === "egg") return "Yumurtalı karışım tamamen pıhtılaşmalı; merkez sıcaklığı 71°C'ye ulaşmalı.";
    if (name.includes("pilav") || name.includes("pirinç")) return "Pilav artarsa hızla soğutup buzdolabına alın; yalnızca bir kez ve her yeri buhar çıkana kadar yeniden ısıtın.";
    return "Pişen yemeği oda sıcaklığında 2 saatten uzun bekletmeyin; artanı sığ bir kapta hızla soğutup buzdolabına alın.";
  }
  if (category === "poultry" || includesAny(name, ["chicken", "turkey", "duck"])) return "Cook poultry to 74°C / 165°F in the thickest part and wash equipment that touched raw poultry.";
  if (category === "groundMeat" || includesAny(name, ["meatball", "meatloaf", "burger", "bolognese", "ragu", "sausage", "bangers", "bratwurst", "kielbasa"])) return "Cook ground meat and sausages to 71°C / 160°F in the centre; no raw mixture should remain.";
  if (category === "fish" || includesAny(name, ["fish", "salmon", "baked cod", "tilapia", "shrimp", "tuna", "seafood", "clam"])) return "Cook fish to 63°C / 145°F, or until opaque and it separates easily with a fork.";
  if (category === "meat") return "Cook whole cuts to at least 63°C / 145°F and rest for 3 minutes; ground meat must reach 71°C / 160°F.";
  if (category === "egg") return "Cook egg dishes until fully set and the centre reaches 71°C / 160°F.";
  if (name.includes("rice")) return "Cool leftover rice quickly, refrigerate promptly, and reheat only once until steaming hot throughout.";
  return "Do not leave cooked food at room temperature for more than 2 hours; cool leftovers quickly in a shallow container.";
};

const storageFor = (category, dishName, locale) => {
  const name = normalize(dishName, locale);
  if (locale === "tr") {
    if (category === "side" && includesAny(name, ["salata", "söğüş", "roka", "yeşil"])) return "Sosu ayrı tutarak kapalı kapta buzdolabında en fazla 1 gün saklayın; tekrar ısıtmayın.";
    if (name.includes("pilav") || name.includes("pirinç")) return "Hızla soğutup kapalı kapta buzdolabında en fazla 1 gün saklayın; yalnızca bir kez, tamamen buhar çıkana kadar ısıtın.";
    if (category === "fish") return "Kapalı kapta buzdolabında en fazla 2 gün saklayın; servis öncesi her yeri tamamen ısınana kadar ısıtın.";
    return "Piştikten sonra 2 saat içinde soğutup kapalı kapta buzdolabında en fazla 3 gün saklayın; yeniden ısıtırken her yerinin buhar çıkardığından emin olun.";
  }
  if (category === "side" && includesAny(name, ["salad", "greens", "slaw", "fruit"])) return "Keep dressing separate and refrigerate in a sealed container for up to 1 day; do not reheat.";
  if (name.includes("rice")) return "Cool quickly, refrigerate in a sealed container for up to 1 day, and reheat only once until steaming hot throughout.";
  if (category === "fish") return "Refrigerate in a sealed container for up to 2 days and reheat until steaming hot throughout.";
  return "Cool within 2 hours, refrigerate in a sealed container for up to 3 days, and reheat until steaming hot throughout.";
};

const tipsFor = (category, locale) => {
  const tr = {
    soup: ["Kıvamı pişmenin sonunda sıcak suyla ayarlayın; soğudukça koyulaşacağını unutmayın.", "Tuzu, varsa et suyu ve salçanın tuzunu kontrol ettikten sonra ekleyin."],
    side: ["Yeşillikleri tamamen kurutmak sosun sulanmasını önler.", "Limonlu veya sirkeli sosu servisten hemen önce ekleyin."],
    fish: ["Balığı fazla pişirmemek için en kalın kısmını kontrol edin.", "Limonu pişirme öncesinde çok bekletmeden, servis sırasında da ekleyebilirsiniz."],
    poultry: ["Eşit pişmesi için tavuk parçalarını benzer büyüklükte kesin.", "Pişen tavuğu 3-5 dakika dinlendirmek suyunu içinde tutar."],
    groundMeat: ["Köfte harcını en az 10 dakika dinlendirmek şeklinin korunmasına yardım eder.", "Harca fazla baskı uygulamak köfteyi sertleştirebilir."],
    meat: ["Eti tencereye tek kat hâlinde alıp yüksek ateşte mühürleyin.", "Sıcak su eklemek etin pişmesini kesintiye uğratmaz."],
    starch: ["Hamur veya makarnayı fazla pişirmeyin; son pişme sosla birlikte tamamlanabilir.", "Yoğurtlu sos kullanılıyorsa kesilmemesi için çok sıcak yemeğe doğrudan eklemeyin."],
    legume: ["Kuru bakliyatı bir gece önceden ıslatmak daha dengeli pişmesini sağlar.", "Asitli malzemeleri bakliyat yumuşadıktan sonra ekleyin."],
    egg: ["Yumurtayı düşük-orta ateşte pişirmek daha yumuşak sonuç verir.", "Yumurtayı eklemeden önce sebzelerin fazla suyunu çektirin."],
    vegetable: ["Sebzeleri benzer büyüklükte doğrayarak eşit pişirin.", "Rengini ve dokusunu korumak için gereğinden uzun pişirmeyin."],
  };
  const en = {
    soup: ["Adjust consistency with hot stock near the end; soup thickens as it cools.", "Taste before adding salt, especially when using stock or cheese."],
    side: ["Dry fresh vegetables thoroughly so the dressing clings instead of becoming watery.", "Dress leafy salads immediately before serving."],
    fish: ["Check the thickest part early to avoid overcooking.", "Add fresh lemon and herbs just before serving for a brighter flavour."],
    poultry: ["Cut poultry into even pieces so it cooks at the same rate.", "Rest cooked poultry for 3-5 minutes before slicing."],
    groundMeat: ["Mix only until combined so burgers and meatballs stay tender.", "Chill shaped meatballs or patties for 10 minutes if they feel soft."],
    meat: ["Brown meat in batches so the pan stays hot.", "Use hot stock when topping up a simmering stew."],
    starch: ["Keep pasta or rice slightly firm if it will continue cooking in sauce or the oven.", "Reserve a little cooking water to loosen sauces without diluting flavour."],
    legume: ["Rinse canned legumes well to reduce excess salt.", "Add acidic ingredients after dried legumes have softened."],
    egg: ["Use low to medium heat for a tender egg texture.", "Cook watery vegetables before adding eggs so the dish sets properly."],
    vegetable: ["Cut vegetables evenly for consistent cooking.", "Stop cooking while vegetables still have colour and a little texture."],
  };
  return (locale === "tr" ? tr : en)[category] || (locale === "tr" ? tr.vegetable : en.vegetable);
};

const substitutionsFor = (category, locale) => {
  const tr = {
    soup: ["Et suyu yerine tuzsuz sebze suyu kullanılabilir.", "Krema yerine yoğurt kullanılacaksa kesilmemesi için ılıştırın."],
    side: ["Limon yerine elma veya üzüm sirkesi kullanılabilir.", "Yoğurtlu tariflerde laktozsuz yoğurt tercih edilebilir."],
    fish: ["Aynı kalınlıktaki mevsim balıkları birbiri yerine kullanılabilir.", "Tereyağı yerine zeytinyağı kullanılabilir."],
    poultry: ["Tavuk göğsü yerine kemiksiz but kullanılabilir; pişirme süresini kontrol edin.", "Süt ürünü içeren soslarda laktozsuz seçenek kullanılabilir."],
    groundMeat: ["Dana-kuzu karışımı yerine yalnızca dana kıyma kullanılabilir.", "Galeta unu yerine glutensiz ekmek kırıntısı kullanılabilir."],
    meat: ["Dana ve kuzu eti, yemeğe uygun benzer pişirme parçasıyla değiştirilebilir.", "Salça yerine rendelenmiş domates kullanılabilir."],
    starch: ["Normal makarna veya un yerine uygun glutensiz alternatif kullanılabilir.", "Kıyma yerine mantar veya haşlanmış yeşil mercimek kullanılabilir."],
    legume: ["Kuru bakliyat yerine iyice durulanmış konserve kullanılabilir.", "Et eklenmeden de aynı yöntemle hazırlanabilir."],
    egg: ["Tereyağı yerine zeytinyağı kullanılabilir.", "Peynir kullanılacaksa laktozsuz veya bitkisel alternatif seçilebilir."],
    vegetable: ["Tarifteki sebze aynı dokudaki mevsim sebzesiyle değiştirilebilir.", "Salça yerine rendelenmiş domates kullanılabilir."],
  };
  const en = {
    soup: ["Use low-salt vegetable stock instead of meat stock.", "Use dairy-free cream or tempered plain yogurt where appropriate."],
    side: ["Use apple cider vinegar instead of lemon juice.", "Choose lactose-free yogurt for yogurt-based dips."],
    fish: ["Swap for another fish fillet of similar thickness.", "Use olive oil instead of butter."],
    poultry: ["Use boneless thighs instead of breast and check doneness by temperature.", "Use a dairy-free sauce where needed."],
    groundMeat: ["Use turkey mince or a plant-based mince if preferred.", "Use gluten-free breadcrumbs where needed."],
    meat: ["Use a similar braising or roasting cut and adjust cooking time.", "Use extra vegetables and beans for a lower-meat version."],
    starch: ["Use a suitable gluten-free pasta, bread or pastry.", "Replace meat fillings with mushrooms or lentils."],
    legume: ["Use well-rinsed canned legumes instead of dried.", "Swap lentils, beans and chickpeas when the texture suits the dish."],
    egg: ["Use olive oil instead of butter.", "Choose lactose-free or dairy-free cheese where appropriate."],
    vegetable: ["Swap for seasonal vegetables with a similar cooking time.", "Use canned tomatoes instead of fresh when out of season."],
  };
  return (locale === "tr" ? tr : en)[category] || (locale === "tr" ? tr.vegetable : en.vegetable);
};

const allergensFor = (dishName, ingredients, locale) => {
  const text = normalize(`${dishName} ${ingredients.join(" ")}`, locale)
    .replace(/coconut milk/g, "coconut beverage");
  const rules = locale === "tr"
    ? [
      [["yoğurt", "süt", "peynir", "tereyağı", "krema"], "Süt"],
      [["un", "ekmek", "makarna", "erişte", "yufka", "bulgur", "galeta"], "Gluten"],
      [["yumurta"], "Yumurta"],
      [["balık", "hamsi", "palamut", "levrek", "çipura", "alabalık", "somon", "uskumru"], "Balık"],
      [["susam", "tahin"], "Susam"],
      [["fındık", "ceviz", "badem", "yer fıstığı"], "Sert kabuklu yemiş"],
    ]
    : [
      [["milk", "cheese", "butter", "cream", "yogurt"], "Milk"],
      [["flour", "bread", "pasta", "noodle", "pastry", "breadcrumbs", "rolls"], "Gluten"],
      [["egg"], "Egg"],
      [["fish", "salmon", "cod", "tilapia", "tuna"], "Fish"],
      [["shrimp", "clam", "seafood"], "Shellfish"],
      [["soy"], "Soy"],
      [["sesame"], "Sesame"],
      [["peanut", "almond", "walnut", "nuts"], "Tree nuts / peanuts"],
      [["mustard"], "Mustard"],
    ];
  return rules.filter(([terms]) => includesAny(text, terms)).map(([, label]) => label);
};

const timeMetadata = (recipe, category, locale) => {
  const total = String(recipe?.sure || (locale === "tr" ? "40 dk" : "40 min"));
  const numbers = total.match(/\d+/g)?.map(Number) || [40];
  const totalMinutes = Math.max(...numbers);
  const prepMinutes = category === "side" ? 10 : category === "soup" ? 15 : category === "meat" ? 20 : 15;
  const cookMinutes = Math.max(5, totalMinutes - prepMinutes);
  return {
    prepTime: `${prepMinutes} ${locale === "tr" ? "dk" : "min"}`,
    cookTime: `${cookMinutes} ${locale === "tr" ? "dk" : "min"}`,
    totalTime: total,
    difficultyKey: totalMinutes >= 75 ? "hard" : totalMinutes >= 45 ? "medium" : "easy",
  };
};

const genericMethodSteps = (dishName, category, locale, primary) => {
  const tr = {
    soup: [
      "Soğanı küçük doğrayın; kullanılacak sebze veya bakliyatı yıkayıp eşit boyda hazırlayın.",
      "Yağı orta ateşte ısıtıp soğanı 4-5 dakika, renk almadan yumuşatın; salça veya baharat kullanılacaksa 1 dakika kavurun.",
      `${primary.label} ile sıcak suyu veya suyu ekleyin; kaynayınca yüzeyde oluşan köpüğü alın ve ateşi kısın.`,
      "Malzemeler tamamen yumuşayana kadar kapağı yarı kapalı pişirin; gerekiyorsa blenderdan geçirip sıcak suyla kıvamı ayarlayın.",
      "Tuzunu en son kontrol edin; terbiyeli veya yoğurtluysa kesilmemesi için kaynatmadan ısıtıp servis edin.",
    ],
    side: [
      `${primary.label} malzemesini yıkayın, tamamen kurulayın ve lokmalık biçimde doğrayın.`,
      "Sos için zeytinyağı, limon veya sirke, tuz ve baharatları ayrı bir kapta çırpın.",
      "Yoğurtlu bir eşlikçiyse yoğurdu pürüzsüzleştirip sarımsak ve tuzla karıştırın.",
      "Hazırlanan malzemeleri ezmeden harmanlayın; sulu malzemelerin fazla suyunu süzün.",
      "Sosu servisten hemen önce ekleyin ve 5 dakika dinlendirerek taze servis edin.",
    ],
    fish: [
      "Balığı kâğıt havluyla kurulayın; kılçık kontrolü yapıp eşit kalınlıktaki porsiyonlara ayırın.",
      "Zeytinyağı, tuz, karabiber ve az limonla her yüzünü hafifçe lezzetlendirin.",
      "Tavayı veya fırını önceden ısıtın; balığı sıkıştırmadan tek sıra hâlinde yerleştirin.",
      "Balığın kalınlığına göre kontrollü pişirin; yalnızca bir kez çevirerek dağılmasını önleyin.",
      "Eti matlaşıp çatalla kolay ayrıldığında ocaktan alın; roka, yeşillik ve taze limonla servis edin.",
    ],
    poultry: [
      "Tavuğu eşit büyüklükte kesin; çiğ tavuk için kullandığınız tahta ve bıçağı diğer malzemelerden ayırın.",
      "Tencereyi iyice ısıtıp tavuğu tek kat hâlinde, suyunu salmadan renk alana kadar mühürleyin.",
      "Soğan, biber ve sarımsağı ekleyip 4-5 dakika orta ateşte soteleyin.",
      "Salça, domates, baharat ve gerekiyorsa sıcak suyu ekleyip kapağı kapalı olarak kısık ateşte pişirin.",
      "En kalın parçanın piştiğini sıcaklıkla kontrol edin; sosu koyulaştırıp 3-5 dakika dinlendirin.",
    ],
    groundMeat: [
      "Kıyma, rendelenip suyu sıkılmış soğan, yumurta, ekmek kırıntısı ve baharatları homojen olana kadar yoğurun.",
      "Harcı 10-15 dakika buzdolabında dinlendirip eşit büyüklükte şekillendirin veya yemeğin iç harcını hazırlayın.",
      "Tavayı ya da fırını önceden ısıtın; kıymalı karışımı dışı renk alacak şekilde kontrollü pişirin.",
      "Yemek sosluysa domates, salça ve sıcak suyu ekleyip kıvam alana kadar kısık ateşte sürdürün.",
      "Merkezinin tamamen piştiğini kontrol edip ocaktan alın; servis öncesi 3 dakika dinlendirin.",
    ],
    meat: [
      "Eti sinirlerinden temizleyip eşit büyüklükte doğrayın; pişirmeden 10 dakika önce buzdolabından çıkarın.",
      "Kalın tabanlı tencereyi yüksek ateşte ısıtıp eti partiler hâlinde mühürleyin.",
      "Soğan ve biberi ekleyerek yumuşatın; salçayı 1 dakika kavurup sıcak su veya et suyunu ilave edin.",
      "Kapağı kapalı, kısık ateşte et yumuşayana kadar pişirin; su azalırsa yalnızca sıcak su ekleyin.",
      "Tuz ve baharatı son bölümde ayarlayın; sos kıvam alınca eti dinlendirip servis edin.",
    ],
    starch: [
      "Hamur, yufka, makarna veya tahıl malzemesini ölçüp yemeğin yöntemine göre ön hazırlığını yapın.",
      "İç harcı veya sosu ayrı tavada; soğan, sebze ve kullanılacak protein tamamen pişecek şekilde hazırlayın.",
      "Makarna veya tahılı diri kalacak biçimde haşlayın; hamurlu yemekte parçaları eşit kalınlıkta açıp şekillendirin.",
      "Hazırlanan harçla birleştirin; fırın yemeğiyse önceden ısıtılmış fırında üzeri kızarana kadar pişirin.",
      "Kıvamını kontrol edip 5 dakika dinlendirin; yoğurt, sos veya taze yeşillikle sıcak servis edin.",
    ],
    legume: [
      "Kuru bakliyatı ayıklayıp yıkayın; mümkünse 8-12 saat suda bekletip suyunu değiştirerek önceden haşlayın.",
      "Soğanı yağda 5 dakika yumuşatın; salçayı ekleyip çiğ kokusu çıkana kadar kavurun.",
      `${primary.label} ekleyip 2 dakika karıştırın; üzerini 2-3 cm geçecek kadar sıcak su ilave edin.`,
      "Kaynadıktan sonra ateşi kısın ve bakliyat ezilmeden tamamen yumuşayana kadar pişirin.",
      "Tuzunu bakliyat yumuşadıktan sonra ayarlayın; ocaktan alınca 10 dakika dinlendirip servis edin.",
    ],
    egg: [
      "Sebzeleri küçük doğrayın; yumurtaları ayrı bir kapta tuz ve baharatla yalnızca birleşene kadar çırpın.",
      "Tereyağı veya zeytinyağını orta ateşte ısıtıp sebzeleri fazla suyunu çekene kadar pişirin.",
      "Ateşi düşük-orta seviyeye getirip yumurtayı tavaya eşit biçimde dökün.",
      "Kenarlar pıhtılaşınca ortasını kontrollü pişirin; gerekiyorsa kapağı kısa süre kapatın.",
      "Yumurta tamamen pıhtılaştığında ocaktan alın; pul biber ve taze ekmekle sıcak servis edin.",
    ],
    vegetable: [
      `${primary.label} malzemesini yıkayıp ayıklayın ve eşit büyüklükte doğrayın.`,
      "Soğanı yağda 4-5 dakika yumuşatın; salça veya domatesi ekleyip kısa süre kavurun.",
      "Sebzeleri sertlik sırasına göre tencereye alın ve 2-3 dakika nazikçe çevirin.",
      "Sıcak suyu ekleyip kapağı kapatın; sebzeler dağılmadan yumuşayana kadar kısık ateşte pişirin.",
      "Tuz ve baharatını ayarlayın; zeytinyağlıysa tenceresinde ılındırıp, sıcak yemekteyse 5 dakika dinlendirip servis edin.",
    ],
  };

  const en = {
    soup: [
      "Dice the onion and prepare the vegetables or legumes in even pieces.",
      "Heat the oil over medium heat; soften the onion for 4-5 minutes, then cook any paste or spices for 1 minute.",
      `Add the ${primary.label} and hot stock; bring to a boil, skim if needed, then reduce to a gentle simmer.`,
      "Cook partly covered until fully tender; blend if the soup should be smooth and adjust with hot stock.",
      "Taste for seasoning at the end and warm dairy-based additions gently without a hard boil.",
    ],
    side: [
      `Wash, dry and cut the ${primary.label} into even bite-size pieces.`,
      "Whisk olive oil, lemon juice or vinegar, salt and pepper in a separate bowl.",
      "For a creamy side, mix the yogurt or dressing until smooth before adding the prepared ingredients.",
      "Combine gently and drain any excess liquid so the finished side stays fresh rather than watery.",
      "Add dressing immediately before serving and rest for 5 minutes for the flavours to settle.",
    ],
    fish: [
      "Pat the fish or seafood dry, check for bones or shell, and portion it evenly.",
      "Season lightly with oil, salt, pepper and lemon without leaving it in acid for too long.",
      "Preheat the pan or oven fully and arrange the fish in a single uncrowded layer.",
      "Cook according to thickness, turning only when needed so the flesh does not break apart.",
      "Remove when opaque and just flaky; finish with fresh lemon and herbs.",
    ],
    poultry: [
      "Cut the poultry into even pieces and keep the board and knife used for raw poultry separate.",
      "Heat the pan well and brown the poultry in a single layer without overcrowding.",
      "Add onion, garlic and vegetables and cook for 4-5 minutes over medium heat.",
      "Add sauce, spices and hot stock where required; cover and cook gently until tender.",
      "Check the thickest piece with a thermometer, reduce the sauce, then rest for 3-5 minutes.",
    ],
    groundMeat: [
      "Combine mince with finely diced onion, egg, breadcrumbs and seasoning only until evenly mixed.",
      "Chill for 10-15 minutes, then shape evenly or prepare the filling specified by the dish.",
      "Preheat the pan or oven and brown the mixture evenly on all sides.",
      "Add sauce or vegetables where required and continue cooking at a gentle simmer.",
      "Check the centre is fully cooked, then rest for 3 minutes before serving.",
    ],
    meat: [
      "Trim and cut the meat into even pieces; take it from the refrigerator about 10 minutes before cooking.",
      "Heat a heavy pan and brown the meat in batches so it sears rather than steams.",
      "Soften the onion and vegetables, then add seasonings and hot stock or sauce.",
      "Cover and cook gently until the meat is tender, adding only hot liquid if the pan becomes dry.",
      "Adjust seasoning near the end, reduce the sauce and rest the meat before serving.",
    ],
    starch: [
      "Measure and prepare the pasta, rice, bread, pastry or dough specified by the dish.",
      "Cook the filling or sauce separately until the vegetables and any protein are properly done.",
      "Cook pasta or grains until slightly firm, or shape dough and pastry to an even thickness.",
      "Combine with the filling or sauce; if baking, use a preheated oven and cook until bubbling and evenly browned.",
      "Check texture, rest for 5 minutes where needed, and finish with herbs, cheese or the suggested sauce.",
    ],
    legume: [
      "Rinse the legumes; soak dried beans or chickpeas for 8-12 hours and cook them in fresh water until nearly tender.",
      "Soften onion in oil for 5 minutes, then add garlic and spices for 1 minute.",
      `Add the ${primary.label}, tomatoes and hot stock, keeping the liquid just above the ingredients.`,
      "Simmer gently until the legumes are tender but still hold their shape.",
      "Add salt and acidic ingredients near the end, then rest for 10 minutes before serving.",
    ],
    egg: [
      "Prepare the vegetables and whisk the eggs with seasoning only until combined.",
      "Heat butter or oil over medium heat and cook watery vegetables until excess moisture evaporates.",
      "Reduce to low-medium heat and add the egg mixture evenly.",
      "Cook gently until the edges and centre are set, covering briefly only if needed.",
      "Remove as soon as fully set and serve while tender and hot.",
    ],
    vegetable: [
      `Wash, trim and cut the ${primary.label} into even pieces.`,
      "Soften onion in oil for 4-5 minutes; add garlic, tomato or spices and cook briefly.",
      "Add vegetables in order of firmness and stir for 2-3 minutes to coat them evenly.",
      "Add hot stock or sauce, cover and cook gently until tender without becoming mushy.",
      "Adjust seasoning, rest for 5 minutes and finish with fresh herbs before serving.",
    ],
  };

  return (locale === "tr" ? tr : en)[category] || (locale === "tr" ? tr.vegetable : en.vegetable);
};

const buildSteps = (dishName, recipe, category, locale, primary, genericRecipe) => {
  const original = genericRecipe
    ? genericMethodSteps(dishName, category, locale, primary)
    : (recipe?.yapilis || []).map((step) => cleanStep(step, primary, locale)).filter(Boolean);
  const prepByCategory = locale === "tr"
    ? {
      groundMeat: `Başlamadan önce ${dishName} için tüm malzemeleri ölçün; soğan ve sebzeleri doğrayıp kıymayı kullanacağınız ana kadar buzdolabında tutun.`,
      legume: `Başlamadan önce ${dishName} için bakliyatı ayıklayıp yıkayın; kuru bakliyat kullanıyorsanız ıslatma ve ön haşlama süresini planlayın.`,
      egg: `Başlamadan önce ${dishName} için tüm malzemeleri ölçün; yumurtaları ayrı bir kaba kırıp kabuk kontrolü yapın.`,
      starch: `Başlamadan önce ${dishName} için hamur, makarna veya tahıl malzemelerini ölçün; iç harç ve sos malzemelerini ayrı hazırlayın.`,
      side: `Başlamadan önce ${dishName} için taze malzemeleri yıkayıp tamamen kurutun; sos malzemelerini ayrı ölçün.`,
    }
    : {
      groundMeat: `Measure all ingredients for ${dishName}; dice the vegetables and keep the mince refrigerated until the pan is ready.`,
      legume: `Measure all ingredients for ${dishName}; rinse the legumes and allow time to soak and pre-cook them when using dried beans or chickpeas.`,
      egg: `Measure all ingredients for ${dishName}; crack the eggs into a separate bowl and check for shell.`,
      starch: `Measure the pasta, rice, bread, pastry or dough for ${dishName}; prepare filling and sauce ingredients separately.`,
      side: `Wash and thoroughly dry the fresh ingredients for ${dishName}; measure the dressing separately.`,
    };
  const prep = prepByCategory[category] || (locale === "tr"
    ? `Başlamadan önce ${dishName} için tüm malzemeleri ölçün; ${primary.label} malzemesini temizleyip eşit boyutlarda hazırlayın.`
    : `Measure all ingredients for ${dishName}; clean and prepare the ${primary.label} in even pieces.`);
  const control = safetyFor(category, dishName, locale);
  const finish = category === "side"
    ? (locale === "tr"
      ? `${dishName} hazır olduğunda sosunu son kez kontrol edin ve tazeliğini kaybetmeden servis edin.`
      : `When ${dishName} is ready, check the dressing and serve promptly while fresh.`)
    : (locale === "tr"
      ? `${dishName} hazır olduğunda ocağı veya fırını kapatın, gerekiyorsa 3-5 dakika dinlendirin; son kıvam ve tuz kontrolünden sonra önerilen eşlikçilerle servis edin.`
      : `When ${dishName} is ready, remove it from the heat, rest for 3-5 minutes where appropriate, then check seasoning and serve with the suggested sides.`);
  return unique([prep, ...original, control, finish]).slice(0, 10);
};

export function enhanceRecipes(recipes = {}, locale = "tr") {
  return Object.fromEntries(Object.entries(recipes).map(([dishName, recipe]) => {
    const override = (locale === "tr" ? TR_RECIPE_OVERRIDES : EN_RECIPE_OVERRIDES)[dishName];
    const sourceRecipe = override || recipe;
    const category = getCategory(dishName, sourceRecipe, locale);
    const primary = locale === "tr" ? trPrimary(dishName, category) : enPrimary(dishName, category);
    const genericRecipe = !override && isGenericRecipe(sourceRecipe);
    const ingredients = getIngredients(dishName, sourceRecipe, category, locale, primary, genericRecipe);
    const steps = buildSteps(dishName, sourceRecipe, category, locale, primary, genericRecipe);
    const time = timeMetadata(sourceRecipe, category, locale);

    return [dishName, {
      ...sourceRecipe,
      ...time,
      recipeVersion: 2,
      methodCategory: category,
      malzemeler: ingredients,
      yapilis: steps,
      tips: tipsFor(category, locale),
      substitutions: substitutionsFor(category, locale),
      storage: storageFor(category, dishName, locale),
      safetyNote: safetyFor(category, dishName, locale),
      allergens: allergensFor(dishName, ingredients, locale),
    }];
  }));
}

export { PLACEHOLDER_PATTERN };
